import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { of } from 'rxjs';

import { DeviceDetectorService } from 'ngx-device-detector';

import { ELegType } from 'src/app/core/enums/leg-type.enum';
import {
  IMapTripDestination,
  IParsedTripLeg,
} from 'src/app/core/types/interfaces/trip-elements.interface';

import { IRouteBTWLeg, MapService } from 'src/app/core/services/map.service';

import Radar from 'radar-sdk-js';
import RadarMap from 'radar-sdk-js/dist/ui/RadarMap';
import RadarMarker from 'radar-sdk-js/dist/ui/RadarMarker';
import RadarLineFeature from 'radar-sdk-js/dist/ui/RadarLineFeature';

import { Point } from 'maplibre-gl';

@Component({
  standalone: true,
  selector: 'app-preview-map',
  templateUrl: './preview-map.component.html',
  styleUrls: ['../maps.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewMapComponent implements OnDestroy {
  @Input() savedTripView: boolean = false;
  @Input() showMapControls: boolean = true;
  @Output() tripLegs: EventEmitter<IParsedTripLeg[]> = new EventEmitter<
    IParsedTripLeg[]
  >();

  private showedDestinationsRoute: string = '';

  private map: RadarMap;
  private mapMarkers: RadarMarker[] = [];
  private mapLines: RadarLineFeature[] = [];

  private clickListener: () => void | null = null;

  @ViewChild('map', { static: true })
  private mapElement: ElementRef<HTMLElement>;

  readonly #ngZone: NgZone = inject(NgZone);
  readonly #mapService: MapService = inject(MapService);
  readonly #deviceDetectorService: DeviceDetectorService = inject(
    DeviceDetectorService,
  );

  @Input() set countryCoordinates(coordinates: [number, number]) {
    this.initMap(coordinates);
  }

  @Input() set destinationsRoute(destinationsRoute: IMapTripDestination[]) {
    const isEqualDestinationsRouteAsPrev = (): boolean => {
      return this.showedDestinationsRoute === JSON.stringify(destinationsRoute);
    };
    if (
      destinationsRoute &&
      destinationsRoute.length &&
      !isEqualDestinationsRouteAsPrev()
    ) {
      const firstCity = destinationsRoute[0];

      if (this.savedTripView) {
        this.initMap(firstCity.coordinates);
      }

      if (this.mapMarkers.length) {
        this.mapMarkers.forEach((marker) => marker.remove());
      }

      if (this.mapLines.length) {
        this.mapLines.forEach((line) => line.remove());
      }

      destinationsRoute.forEach((d: IMapTripDestination, index: number) => {
        const pointIndex: number = index + 1;
        const roundTrip: boolean = this.isCircularTrip(destinationsRoute);
        const el: HTMLElement = this.#mapService.createSimpleMarkerElement(
          pointIndex,
          roundTrip,
        );
        const marker: any | RadarMarker = Radar.ui.marker({
          element: el,
          anchor: 'center',
        });
        marker._offset = new Point(0, -20);
        marker.setLngLat(d.coordinates).addTo(this.map);
        this.mapMarkers.push(marker);
      });

      this.map.fitToMarkers({
        padding: 65,
      });
      this.getPathRoutesByCities(destinationsRoute);
      this.showedDestinationsRoute = JSON.stringify(destinationsRoute);
    }
  }

  ngOnDestroy(): void {
    const userAgent: string = this.#deviceDetectorService.userAgent;
    if (userAgent) {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      if (this.clickListener) {
        this.clickListener();
        this.clickListener = null;
      }
    }
  }

  private initMap(coordinates: [number, number]): void {
    const userAgent: string = this.#deviceDetectorService.userAgent;

    if (userAgent && !this.map) {
      const { nativeElement } = this.mapElement;
      this.#ngZone.runOutsideAngular(() => {
        this.map = Radar.ui.map({
          container: nativeElement,
          style: 'radar-default-v1',
          showZoomControls: this.showMapControls,
          center: coordinates,
          zoom: 5,
          scrollZoom: false,
          trackResize: true,
        });
      });

      if (this.showMapControls) {
        const fitToZoomCtrlEl = this.#mapService.createFitToZoomCtrlElement();
        const controlGroupEl = nativeElement.querySelector(
          '.maplibregl-ctrl.maplibregl-ctrl-group',
        );
        controlGroupEl.prepend(fitToZoomCtrlEl);

        this.clickListener = () =>
          fitToZoomCtrlEl.removeEventListener(
            'click',
            this.handleFitToZoomClick.bind(this),
          );
        fitToZoomCtrlEl.addEventListener(
          'click',
          this.handleFitToZoomClick.bind(this),
        );
      }
    }
  }

  private handleFitToZoomClick(): void {
    if (this.map) {
      this.map.fitToMarkers({ padding: 65 });
    }
  }

  /**
   * Determines route segments between cities based on provided coordinates and creates polyline or geodesic arcs on the map.
   * The function calculates the distance and travel time between route points, updating routes for car or flight segments.
   * Used to display routes with both driving and flight legs on the map.
   *
   * @param destinationsRoute - Array of route segments where each segment includes:
   *   - `coordinates`: an array of numbers representing the latitude and longitude of the destination point.
   *   - `nextLegIsFlight`: a boolean indicating whether the next segment is a flight.
   *
   * This function performs the following tasks:
   * 1. Maps each segment to an object containing `dist` (distance), `time`, and `type` (either car or plane).
   * 2. Iterates through each destination to build route segments, accumulating coordinates until reaching a flight segment or the last point.
   * 3. If the segment ends with a car trip, it requests directions and calculates the distance and time for each leg, then adds polyline to the map.
   * 4. If the segment ends with a flight, it calculates direct distance between points and creates a geodesic arc on the map.
   *
   * Additional functionality:
   * - Calls to external map services are delayed slightly to avoid overlap.
   * - Updates the `tripLegs` observable with the computed routes after each segment's details are fetched.
   */
  private getPathRoutesByCities(
    destinationsRoute: IMapTripDestination[],
  ): void {
    let currentRouteSegment: number[][] = [];
    const pairsRouteSegments: number[][] = [];
    const routes: IParsedTripLeg[] = destinationsRoute.map((r) => ({
      dist: 'N/A',
      time: 'N/A',
      type: !r.nextLegIsFlight ? ELegType.CAR : ELegType.PLANE,
      geometryString: '',
    }));
    const setPolyline = (leg: IRouteBTWLeg, legIndex: number) => {
      routes[legIndex].dist = (leg.distance.value / 1000).toFixed(2) + ' km';
      routes[legIndex].time = leg.duration.text;
      routes[legIndex].geometryString = leg.geometry.polyline;
      const polyline: RadarLineFeature = this.map.addPolyline(
        leg.geometry.polyline,
        this.#mapService.polylinePathOptions,
      );
      polyline.id = `polyline-feature-${this.#mapService.generateUniqueId()}`;
      this.mapLines.push(polyline);
      this.tripLegs.next(routes);
    };
    const setArcLine = (
      pointACoords: number[],
      pointBCoords: number[],
      legIndex: number,
    ) => {
      routes[legIndex].dist =
        this.#mapService.getDistance(pointACoords, pointBCoords).toFixed(2) +
        ' km';
      setTimeout(() => {
        const geodesicArc = this.#mapService.createGeodesicArc([
          pointACoords,
          pointBCoords,
        ]);
        routes[legIndex].type = ELegType.PLANE;
        routes[legIndex].geometryString = JSON.stringify(geodesicArc);
        const arcLine = this.map.addLine(geodesicArc.line, geodesicArc.options);
        this.mapLines.push(arcLine);
        this.tripLegs.next(routes);
      }, legIndex * 25);
    };

    destinationsRoute.forEach((destinationRoute, index) => {
      const { coordinates, nextLegIsFlight } = destinationRoute;
      const lastIndex: boolean = index === destinationsRoute.length - 1;
      currentRouteSegment.push(coordinates);
      pairsRouteSegments.push(coordinates);

      if (nextLegIsFlight || lastIndex) {
        if (currentRouteSegment.length > 1) {
          const routeString = currentRouteSegment
            .map((coords) => [...coords].reverse().join(','))
            .join('|');
          this.#mapService.getDirectionsRoutesAndLegs(routeString).subscribe({
            next: (legs) => {
              legs.forEach((leg, lIndex) => {
                const correctLegIndex = index - (legs.length - lIndex);
                setPolyline(leg, correctLegIndex);
              });
            },
            error: (err) => {
              for (let i = 0; i < pairsRouteSegments.length - 1; i++) {
                const pairSegmentA = pairsRouteSegments[i];
                const pairSegmentB = pairsRouteSegments[i + 1];
                const segment = `${[...pairSegmentA].reverse()}|${[...pairSegmentB].reverse()}`;
                this.#mapService.getDirectionsRoutesAndLegs(segment).subscribe({
                  next: (legs) => setPolyline(legs[0], i),
                  error: () => setArcLine(pairSegmentA, pairSegmentB, i),
                });
              }
              throw of(err);
            },
          });
        }

        if (nextLegIsFlight && !lastIndex) {
          const nextCoordinates = destinationsRoute[index + 1].coordinates;
          setArcLine(coordinates, nextCoordinates, index);
        }

        currentRouteSegment = nextLegIsFlight ? [] : [coordinates];
      }
    });
  }

  private isCircularTrip(destinationsRoute: IMapTripDestination[]): boolean {
    const first: string = destinationsRoute[0].coordinates.join(',');
    const last: string =
      destinationsRoute[destinationsRoute.length - 1].coordinates.join(',');

    return first === last;
  }
}
